// Interactive bitrate/LPF tradeoff demo, hydrated into blog post via gatsby-browser.js
import React from 'react'

class AacDemo extends React.Component {
  constructor(props) {
    super(props)
    this.canvasRef = React.createRef()
    this.animTime = 0
    this.frozen = false
    this.animId = null
    this.state = { bitrate: 50, lpf: 100, frozen: false }
  }

  componentDidMount() {
    this.animate()
  }

  componentWillUnmount() {
    if (this.animId) cancelAnimationFrame(this.animId)
  }

  // Fake spectrum: two Gaussian humps (like a typical mix) plus animated harmonics
  signalCurve(x) {
    const t = this.animTime
    const envelope = 0.12 + 0.7 * Math.exp(-0.5 * Math.pow((x - 0.3) / 0.22, 2))
                   + 0.25 * Math.exp(-0.5 * Math.pow((x - 0.68) / 0.15, 2))
    const wobble = 0.06 * Math.sin(x * Math.PI * 7.3 + t * 0.7)
                 + 0.05 * Math.sin(x * Math.PI * 13.7 + t * 1.1)
                 + 0.04 * Math.sin(x * Math.PI * 22.1 + t * 0.5)
                 + 0.03 * Math.sin(x * Math.PI * 31.4 + t * 1.4)
    return Math.max(0.08, Math.min(0.95, envelope + wobble))
  }

  // The draw loop paints a simplified model of AAC encoding:
  //  1. Compute bit budget: how many quantisation levels per band given the sliders
  //  2. Sample the signal curve per-pixel, snap to nearest level (= quantise)
  //  3. Draw layers bottom-up: grid lines, true signal, quantized staircase, LPF cutoff
  //  4. Estimate total error and render the artefact bar at the bottom
  draw() {
    const canvas = this.canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const { bitrate, lpf } = this.state
    const NUM_BANDS = 80

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const marginLeft = 50, marginBottom = 80, marginTop = 30, marginRight = 30
    const plotW = canvas.width - marginLeft - marginRight
    const plotH = canvas.height - marginTop - marginBottom
    const plotBottom = marginTop + plotH

    // Bit budget model: total levels scale with bitrate slider.
    // LPF kills upper bands, redistributing their bits to remaining bands (boost).
    const totalLevels = 200 + bitrate * 12
    const lpfNorm = lpf / 100
    const lpfFrac = 0.75 + 0.25 * Math.pow(lpfNorm, 0.5)
    const activeBands = Math.max(1, Math.round(lpfFrac * NUM_BANDS))
    const fracCut = 1 - activeBands / NUM_BANDS
    const boost = Math.pow(2, fracCut * 4)
    const effectiveLevels = totalLevels * boost
    const lvlExact = Math.max(2, effectiveLevels / activeBands)
    const bandWidth = plotW / NUM_BANDS

    const valToY = v => plotBottom - v * plotH * 0.9 - plotH * 0.05

    // Sample signal at each pixel, quantise to nearest level within active bands
    const totalPx = Math.floor(plotW)
    const truePoints = [], quantizedPoints = []
    for (let px = 0; px <= totalPx; px++) {
      const x = marginLeft + px
      const frac = px / totalPx
      const val = this.signalCurve(frac)
      const bandIdx = Math.min(NUM_BANDS - 1, Math.floor(frac * NUM_BANDS))
      const trueY = valToY(val)
      let quantY = trueY, quantVal = val
      if (bandIdx < activeBands) {
        const step = 1.0 / lvlExact
        quantVal = Math.round(val / step) * step
        quantVal = Math.min(1, Math.max(0, quantVal))
        quantY = valToY(quantVal)
      }
      truePoints.push({ x, y: trueY })
      quantizedPoints.push({ x, y: quantY })
    }

    // Shade killed bands
    for (let i = 0; i < NUM_BANDS; i++) {
      if (i >= activeBands) {
        const x0 = marginLeft + i * bandWidth
        ctx.fillStyle = 'rgba(0,0,0,0.03)'
        ctx.fillRect(x0, marginTop, bandWidth, plotH)
      }
    }

    // Quantisation grid: horizontal lines showing available levels per band
    ctx.strokeStyle = 'rgba(200, 200, 200, 0.6)'
    ctx.lineWidth = 0.7
    const numLines = Math.floor(lvlExact)
    for (let l = 0; l <= numLines; l++) {
      const val = l / lvlExact
      if (val > 1) break
      const y = valToY(val)
      for (let i = 0; i < activeBands; i++) {
        const x0 = marginLeft + i * bandWidth
        const x1 = x0 + bandWidth
        ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(x1, y); ctx.stroke()
      }
    }

    // True signal (smooth curve)
    ctx.strokeStyle = 'rgba(44, 123, 182, 0.9)'
    ctx.lineWidth = 2.5
    ctx.beginPath()
    for (let px = 0; px <= totalPx; px++) {
      const pt = truePoints[px]
      if (px === 0) ctx.moveTo(pt.x, pt.y); else ctx.lineTo(pt.x, pt.y)
    }
    ctx.stroke()

    // Draw quantized signal as staircase steps (snapped to grid lines)
    ctx.strokeStyle = 'rgba(215, 25, 28, 0.8)'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(quantizedPoints[0].x, quantizedPoints[0].y)
    let prevQY = quantizedPoints[0].y
    for (let px = 1; px <= totalPx; px++) {
      const pt = quantizedPoints[px]
      if (Math.abs(pt.y - prevQY) > 0.5) {
        ctx.lineTo(pt.x, prevQY); ctx.lineTo(pt.x, pt.y); prevQY = pt.y
      }
    }
    ctx.lineTo(quantizedPoints[totalPx].x, prevQY)
    ctx.stroke()

    // Band separators (vertical grid)
    ctx.strokeStyle = 'rgba(0,0,0,0.06)'
    ctx.lineWidth = 0.5
    for (let i = 0; i <= NUM_BANDS; i++) {
      const sx = marginLeft + i * bandWidth
      ctx.beginPath(); ctx.moveTo(sx, marginTop); ctx.lineTo(sx, plotBottom); ctx.stroke()
    }

    // LPF cutoff line and shaded "removed" region
    const lpfX = marginLeft + lpfFrac * plotW
    if (activeBands < NUM_BANDS) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)'
      ctx.fillRect(lpfX, marginTop, marginLeft + plotW - lpfX, plotH)
    }

    ctx.strokeStyle = 'rgba(215, 25, 28, 0.9)'
    ctx.lineWidth = 2
    ctx.setLineDash([6, 4])
    ctx.beginPath(); ctx.moveTo(lpfX, marginTop); ctx.lineTo(lpfX, plotBottom); ctx.stroke()
    ctx.setLineDash([])

    ctx.fillStyle = 'rgba(215, 25, 28, 0.9)'
    ctx.font = 'bold 10px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('LPF', lpfX, marginTop - 8)

    ctx.fillStyle = '#888'
    ctx.font = '11px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Frequency →', marginLeft + plotW / 2, plotBottom + 18)

    ctx.save()
    ctx.translate(15, marginTop + plotH / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.fillStyle = '#888'
    ctx.font = '11px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('Amplitude', 0, 0)
    ctx.restore()

    // Error estimate: average step size as fraction of worst-case (lowest bitrate, no LPF)
    const stepSize = 1.0 / lvlExact
    const activeError = activeBands * (stepSize / 4)
    const worstTotal = 200 + 25 * 12
    const worstLvl = Math.max(2, worstTotal / NUM_BANDS)
    const worstStep = 1.0 / worstLvl
    const worstError = NUM_BANDS * (worstStep / 4)
    const errorFrac = Math.min(1, activeError / worstError)

    const barY = canvas.height - 30, barH = 14, barW = plotW, barX = marginLeft
    ctx.fillStyle = 'rgba(0,0,0,0.04)'
    ctx.fillRect(barX, barY, barW, barH)
    const errorGrad = ctx.createLinearGradient(barX, 0, barX + barW, 0)
    errorGrad.addColorStop(0, 'rgba(215, 25, 28, 0.4)')
    errorGrad.addColorStop(1, 'rgba(215, 25, 28, 0.9)')
    ctx.fillStyle = errorGrad
    ctx.fillRect(barX, barY, barW * errorFrac, barH)
    ctx.strokeStyle = 'rgba(0,0,0,0.1)'
    ctx.lineWidth = 1
    ctx.strokeRect(barX, barY, barW, barH)
    ctx.fillStyle = '#555'
    ctx.font = 'bold 11px sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText('ENCODING ARTEFACTS', barX, barY - 5)
  }

  animate() {
    if (!this.state.frozen) this.animTime += 0.025
    this.draw()
    this.animId = requestAnimationFrame(() => this.animate())
  }

  render() {
    const { bitrate, lpf, frozen } = this.state
    const styles = {
      wrapper: { background: '#fafafa', padding: '1rem', borderRadius: '6px', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center' },
      controls: { display: 'flex', gap: '2rem', marginBottom: '1rem', flexWrap: 'wrap', justifyContent: 'center' },
      controlGroup: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' },
      label: { fontSize: '0.75rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 },
      range: { width: '200px', accentColor: '#2c7bb6' },
      canvasWrap: { position: 'relative', display: 'inline-block' },
      canvas: { border: '1px solid #ccc', borderRadius: '4px', background: '#fff', maxWidth: '100%' },
      freezeBtn: { position: 'absolute', bottom: '90px', right: '12px', background: 'rgba(0,0,0,0.04)', border: '1px solid ' + (frozen ? 'rgba(44,123,182,0.5)' : 'rgba(0,0,0,0.15)'), color: '#666', padding: '0.2rem 0.5rem', borderRadius: '3px', cursor: 'pointer', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
      legend: { display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.75rem', color: '#333' },
      legendItem: { display: 'flex', alignItems: 'center', gap: '0.4rem' },
      swatch: w => ({ width: '12px', height: '12px', borderRadius: '2px', background: w, border: '1px solid rgba(0,0,0,0.1)' })
    }

    return (
      <div style={styles.wrapper}>
        <div style={styles.controls}>
          <div style={styles.controlGroup}>
            <label style={styles.label}>Bitrate</label>
            <input type="range" min="25" max="100" step="1" value={bitrate} style={styles.range}
              onChange={e => this.setState({ bitrate: parseInt(e.target.value) })} />
          </div>
          <div style={styles.controlGroup}>
            <label style={styles.label}>Low-pass filter</label>
            <input type="range" min="0" max="100" step="1" value={lpf} style={styles.range}
              onChange={e => this.setState({ lpf: parseInt(e.target.value) })} />
          </div>
        </div>
        <div style={styles.canvasWrap}>
          <canvas ref={this.canvasRef} width={900} height={540} style={styles.canvas} />
          <button style={styles.freezeBtn} onClick={() => this.setState({ frozen: !frozen })}>
            {frozen ? '▶ Play' : '⏸ Freeze'}
          </button>
        </div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={styles.swatch('rgba(44,123,182,0.9)')}></div>
            <span>True signal</span>
          </div>
          <div style={styles.legendItem}>
            <div style={styles.swatch('rgba(215,25,28,0.8)')}></div>
            <span>Quantized signal</span>
          </div>
        </div>
      </div>
    )
  }
}

export default AacDemo
